import {
  initRecoveryPassword,
  useApi,
  RecoveryForm as RecoveryFormApi,
  useToggle,
  removeUrlParameters,
} from '@repo-level/core-front';
import { FC, forwardRef, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormApi } from 'informed';

import { Button } from 'components/buttons';
import { ErrorGroup } from 'components/form/groups/ErrorGroup';
import { FormGroup } from 'components/form/groups/FormGroup';
import { ScFormSubmitGroup } from 'components/form/groups/styled';
import { Input } from 'components/form/Input';
import { ModalDefaultProps } from 'components/Modal';
import { mailMask } from 'helpers/masks';
import { showTooltip } from 'helpers/tooltip';
import { translationFactory } from 'helpers/trans';
import { validateEmail } from 'helpers/validators';
import { TooltipTypes } from 'modules/globalTooltip';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { getQueryParameter, localStorageEmail } from 'helpers/common';

import { ScRecoveryText } from './styled';
import { useMedia } from 'modules/common/hooks/useMedia';

export const Recovery = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const { t } = useTranslation('common');

    const { isMobileS } = useMedia();

    const [isSuccessRecovery, toggleIsSuccessRecovery] = useToggle();

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? { type: 'mobile', mode: { type: 'default' }, headlineGapSm: true }
        : {
            type: 'desktop',
            size: 'small',
            mode: { type: 'default' },
            headlineGapSm: true,
          };
    }, [isMobileS]);

    const handleCloseModal = () => {
      removeUrlParameters(['email']);
      if (onClose) onClose();
    };

    return (
      <ModalTemplate
        ref={ref}
        onClose={handleCloseModal}
        settings={modalSettings}
        title={!isSuccessRecovery ? t('auth.recovery.title') : null}
        description={!isSuccessRecovery ? t('auth.recovery.description') : null}
      >
        {isSuccessRecovery ? (
          <ScRecoveryText>{t('auth.recovery.checkEmail')}</ScRecoveryText>
        ) : (
          <RecoveryForm toggleSuccessRecovery={toggleIsSuccessRecovery} />
        )}
      </ModalTemplate>
    );
  }
);

const RecoveryForm: FC<{ toggleSuccessRecovery: () => void }> = ({
  toggleSuccessRecovery,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'glossary']);
  const formApiRef = useRef<FormApi<RecoveryFormApi>>();

  const [requestRecoveryPassword, { error: recoveryError, pending }] = useApi(
    initRecoveryPassword
  );

  const handleSubmit = async (values: RecoveryFormApi) => {
    const { error } = await requestRecoveryPassword(values).request;

    if (error != null) {
      showTooltip({
        type: TooltipTypes.ERROR,
        translate: true,
        title: translationFactory('auth.recovery.error'),
      });

      return;
    }

    localStorageEmail.push(values.email);

    toggleSuccessRecovery();
  };

  const email = getQueryParameter('email');

  useEffect(() => {
    if (email) formApiRef.current?.setValue('email', email);
  }, [email]);

  return (
    <Form<RecoveryFormApi>
      getApi={(api) => {
        formApiRef.current = api;
      }}
      onSubmit={handleSubmit}
    >
      <FormGroup>
        <Input
          autoComplete="off"
          animatedPlaceholder={t('glossary:email')}
          validateOnBlur
          field="email"
          validate={validateEmail}
          mask={mailMask}
          initialValue={`${localStorageEmail.get()}`}
        />
      </FormGroup>

      {recoveryError != null && (
        <ErrorGroup>{t('auth.recovery.invalidEmail')}</ErrorGroup>
      )}

      <ScFormSubmitGroup className={language.toLowerCase()}>
        <Button green xLarge wide pending={pending} type="submit">
          {t('auth.recovery.resetPassword')}
        </Button>
      </ScFormSubmitGroup>
    </Form>
  );
};
